import { useState, useRef, useEffect } from "react";
import axios from "@/services/axios";
import {
  ArrowLeft,
  Clock,
  BookOpen,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

import SlideVideoPlayer from "./SlideVideoPlayer";
import QuizComponent from "./QuizComponent";
import ModuleProgress from "./ModuleProgress";

import { toast } from "../../utils/notify";
import { generateCertificate } from "./certificateGenerator";
import { useQuizAnalytics } from "./QuizAnalyticsContext";

/* ======================================================================
   LOCAL UI COMPONENTS (Button, Badge, Collapsible)
====================================================================== */

/* ---------- Button ---------- */
const Button = ({
  children,
  className = "",
  variant = "default",
  size = "default",
  ...props
}) => {
  const base =
    "inline-flex items-center justify-center rounded-md text-sm font-medium transition disabled:opacity-50 disabled:pointer-events-none";

  const variants = {
    default: "bg-blue-600 text-white hover:bg-blue-700",
    ghost: "hover:bg-gray-100 text-gray-700",
    outline: "border border-gray-300 hover:bg-gray-100",
  };

  const sizes = {
    default: "h-10 px-4",
    icon: "h-10 w-10",
    sm: "h-8 px-3 text-xs",
  };

  return (
    <button
      className={`${base} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

/* ---------- Badge ---------- */
const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-semibold text-gray-700">
    {children}
  </span>
);

/* ---------- Collapsible ---------- */
const Collapsible = ({ open, onOpenChange, children }) => (
  <div>{children(open, onOpenChange)}</div>
);

/* ======================================================================
   MODULE DETAILS
====================================================================== */

const ModuleDetails = ({ moduleId, employeeId, userRole, onBack, onMoveQuiz, onLoaded }) => {
  const [module, setModule] = useState(null);
  const [sections, setSections] = useState([]);
  const [quiz, setQuiz] = useState([]);

  const [videoProgress, setVideoProgress] = useState(0);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizScore, setQuizScore] = useState(null);
  const [passed, setPassed] = useState(false);
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);

  const quizStartTime = useRef(null);

  /* ------------------------------------------------------------------
     FETCH DATA FROM BACKEND
  ------------------------------------------------------------------ */
useEffect(() => {
  if (!moduleId) return;

  const loadData = async () => {
    try {
      const [moduleRes, sectionRes] = await Promise.all([
        axios.get(`/api/lms/modules/${moduleId}/`),
        axios.get(`/api/lms/modules/${moduleId}/sections/`),
      ]);

      setModule(moduleRes.data);

      // handle paginated & non-paginated safely
      setSections(sectionRes.data?.results || sectionRes.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load module");
    } finally {
      // 🔥 THIS IS THE KEY LINE
      if (onLoaded) onLoaded();
    }
  };

  loadData();
}, [moduleId, onLoaded]);


useEffect(() => {
  if (!employeeId || !moduleId) return;


  const loadStatus = async () => {
    try {
      const res = await axios.get(
        `/api/lms/status/${employeeId}/${moduleId}`
      );

      if (res.data.video_seen) {
        setVideoCompleted(true);
        setVideoProgress(100);
      }
    } catch (err) {
      console.error("Failed to load video status", err);
    }
  };

  loadStatus();
}, [employeeId, moduleId]);

  /* ------------------------------------------------------------------
     VIDEO HANDLERS
  ------------------------------------------------------------------ */
  const handleVideoProgress = (progress) => {
    setVideoProgress(progress);
    if (progress >= 100) setVideoCompleted(true);
  };

const handleVideoComplete = async () => {
  setVideoProgress(100);
  setVideoCompleted(true);

  // 🔥 ONLY end_user should call completion API
  if (userRole !== "end_user") {
    return;
  }

  if (!employeeId || !module?._id) {
    console.warn("Missing employeeId or moduleId");
    return;
  }

  try {
    const res = await axios.post("/api/lms/mark-completed/", {
      employee_id: employeeId,
      lms_id: module._id
    });

    console.log("Completion updated:", res.data);
  } catch (err) {
    console.error("Failed to update LMS completion", err);
  }
};

  useEffect(() => {
    if (videoCompleted && !quizStartTime.current) {
      quizStartTime.current = Date.now();
    }
  }, [videoCompleted]);

  /* ------------------------------------------------------------------
     QUIZ COMPLETE
  ------------------------------------------------------------------ */
  const handleQuizComplete = async (score, hasPassed) => {
    setQuizCompleted(true);
    setQuizScore(score);
    setPassed(hasPassed);

    const timeSpent = quizStartTime.current
      ? Math.round((Date.now() - quizStartTime.current) / 1000)
      : 300;


    if (hasPassed) {
      toast.success("Congratulations! Generating your certificate…");

      await generateCertificate(
        module.moduleTitle,
        module._id,
        score,
        module.isoAlignment || []
      );
    } else {
      toast.error(`Quiz failed: you scored ${score}%. 60% is required.`);
    }
  };

  if (!module) {
    return <div className="py-20 text-center text-gray-500"></div>;
  }

  /* ------------------------------------------------------------------
     UI
  ------------------------------------------------------------------ */

  const isAdmin =
  userRole === "admin" || userRole === "platform_admin";

const isDisabled =
  userRole === "end_user" && !videoCompleted;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="flex items-center gap-4 border-b px-4 py-3">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="w-5 h-5" />
        </Button>

        <div>
          <h1 className="font-semibold">{module.moduleTitle}</h1>
          <div className="text-sm text-gray-500 flex gap-4">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" /> {module.duration || "45 min"}
            </span>
            <span className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" /> {quiz.length} Questions
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 grid lg:grid-cols-3 gap-8">
        {/* MAIN */}
        <div className="lg:col-span-2 space-y-8">
          <SlideVideoPlayer
            moduleId={module._id}
            moduleTitle={module.moduleTitle}
            category={module.category}
            sections={sections}
            onProgress={handleVideoProgress}
            onComplete={handleVideoComplete}
          />

          {/* About */}
          {/* <div className="border rounded-xl p-6">
            <h3 className="font-semibold mb-2">About This Module</h3>
            <p className="text-gray-600">{module.description}</p>

            {module.isoAlignment && (
              <div className="mt-4 flex gap-2 flex-wrap">
                {module.isoAlignment.map((iso) => (
                  <Badge key={iso}>{iso}</Badge>
                ))}
              </div>
            )}
          </div> */}

          {/* Transcript */}
          {/* <div className="border rounded-xl">
            <button
              onClick={() => setIsTranscriptOpen(!isTranscriptOpen)}
              className="w-full flex justify-between p-4 font-medium"
            >
              Video Transcript
              {isTranscriptOpen ? <ChevronUp /> : <ChevronDown />}
            </button>

            {isTranscriptOpen && (
              <div className="border-t p-4 text-sm text-gray-600">
                Transcript content will be loaded from slides.
              </div>
            )}
          </div> */}

          {/* Quiz */}
          {/* <QuizComponent
            questions={quiz.map((q) => ({
              question: q.question,
              options: q.options,
              correctAnswer: q.correct_answer,
            }))}
            passingScore={70}
            onComplete={handleQuizComplete}
            isUnlocked={videoCompleted}
          /> */}
        </div>

        <div className="space-y-6">
  {/* Progress Tracker */}
  <ModuleProgress
    videoProgress={videoProgress}
    quizCompleted={quizCompleted}
    quizScore={quizScore}
    passed={passed}
  />

  {/* Learning Objectives */}
  <div className="bg-white rounded-xl border p-6">
    <h3 className="font-semibold mb-4">Learning Objectives</h3>
    <ul className="space-y-3 text-sm text-gray-600">
      <li className="flex items-start gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-2" />
        Understand key cybersecurity concepts
      </li>
      <li className="flex items-start gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-2" />
        Identify common threats and attack vectors
      </li>
      <li className="flex items-start gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-2" />
        Apply security best practices
      </li>
      <li className="flex items-start gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-2" />
        Respond correctly to incidents
      </li>
    </ul>
  </div>
<button
  onClick={() => {
    if (isDisabled) return;
    onMoveQuiz(module._id);
  }}
  disabled={isDisabled}
  className={`
    w-full mt-4 py-3 rounded-lg font-semibold transition
    ${
      isDisabled
        ? "bg-gray-400 text-white cursor-not-allowed"
        : "bg-blue-600 text-white hover:bg-blue-700"
    }
  `}
>
  Move to Quiz
</button>
</div>



      </div>
    </div>
  );
};

export default ModuleDetails;